import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Music, Disc, Download, Loader2, CreditCard, 
  CheckCircle, AlertCircle, Play, Pause, Lock, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Initialise Stripe once, synchronously, at module load. If the key is missing
// stripePromise stays null and the UI shows an "unavailable" message.
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = (STRIPE_KEY && STRIPE_KEY !== 'undefined' && STRIPE_KEY.startsWith('pk_'))
  ? loadStripe(STRIPE_KEY)
  : null;

// Payment Form Component
const PaymentForm = ({ product, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    if (!email) {
      setError('Email is required for receipt');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          email,
          name: name || undefined
        }
      });

      if (pmError) {
        setError(pmError.message);
        setLoading(false);
        return;
      }

      // Process purchase
      const endpoint = product.type === 'track' 
        ? `${API}/api/quick-buy/purchase/track/${product.id}`
        : `${API}/api/quick-buy/purchase/album/${product.id}`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_method_id: paymentMethod.id,
          email,
          name,
          format: product.format || 'mp3'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Payment failed');
      }

      onSuccess(data);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1 text-white">Email *</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="w-full px-4 py-3 bg-stone-800 border border-stone-600 rounded-xl focus:border-primary focus:outline-none text-white"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1 text-white">Name (optional)</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className="w-full px-4 py-3 bg-stone-800 border border-stone-600 rounded-xl focus:border-primary focus:outline-none text-white"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1 text-white">Card Details</label>
        <div className="px-4 py-3 bg-stone-800 border border-stone-600 rounded-xl">
          <CardElement 
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#fff',
                  '::placeholder': { color: '#9ca3af' }
                }
              }
            }}
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || loading}
        className={`w-full py-4 font-bold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
          product.format === 'wav' 
            ? 'bg-purple-600 hover:bg-purple-500 text-white' 
            : 'bg-primary hover:bg-primary/90 text-black'
        }`}
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5" />
            Pay ${product.price?.toFixed(2)} Now
          </>
        )}
      </button>

      <p className="text-xs text-center text-muted-foreground">
        <Lock className="w-3 h-3 inline mr-1" />
        Secure payment powered by Stripe
      </p>
    </form>
  );
};

// Success Component
const DownloadSuccess = ({ data, product }) => {
  const handleDownload = (url, title, ext = 'mp3') => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title}.${ext}`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started!');
  };

  const handleAlbumZipDownload = (format = 'mp3') => {
    if (data.download_url_mp3 || data.download_url_wav) {
      const url = format === 'wav' ? data.download_url_wav : data.download_url_mp3;
      if (url) {
        window.open(url, '_blank');
        toast.success(`Album ZIP download started (${format.toUpperCase()})!`);
      }
    }
  };

  const downloadsRemaining = data.downloads_remaining || 5;

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="text-center"
    >
      <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircle className="w-10 h-10 text-green-500" />
      </div>
      
      <h2 className="text-2xl font-bold text-white mb-2">Purchase Complete!</h2>
      <p className="text-muted-foreground mb-2">Your download is ready</p>
      
      {/* Download limit notice */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-6">
        <p className="text-amber-400 text-sm">
          <strong>{downloadsRemaining} downloads</strong> available for this purchase
        </p>
        <p className="text-amber-400/70 text-xs mt-1">
          Links expire in 30 days
        </p>
      </div>

      {product.type === 'track' ? (
        <button
          onClick={() => handleDownload(data.download_url, data.title)}
          className="w-full py-4 bg-primary text-black font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
        >
          <Download className="w-5 h-5" />
          Download "{data.title}"
        </button>
      ) : (
        <div className="space-y-3">
          {/* Download Complete Album ZIP Button */}
          {(data.download_url_mp3 || data.download_url_wav) && (
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4 mb-4">
              <p className="text-sm text-purple-300 mb-3 font-medium">Download Complete Album</p>
              <div className="flex gap-2">
                {data.download_url_mp3 && (
                  <button
                    onClick={() => handleAlbumZipDownload('mp3')}
                    className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-500 hover:to-pink-500 transition-all flex items-center justify-center gap-2"
                    data-testid="download-album-mp3-btn"
                  >
                    <Disc className="w-4 h-4" />
                    MP3 ZIP
                  </button>
                )}
                {data.download_url_wav && (
                  <button
                    onClick={() => handleAlbumZipDownload('wav')}
                    className="flex-1 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white font-bold rounded-xl hover:from-orange-500 hover:to-red-500 transition-all flex items-center justify-center gap-2"
                    data-testid="download-album-wav-btn"
                  >
                    <Disc className="w-4 h-4" />
                    WAV ZIP
                  </button>
                )}
              </div>
            </div>
          )}

          <p className="text-sm text-muted-foreground mb-2">
            Or download individual tracks ({data.downloads?.length} tracks):
          </p>
          {data.downloads?.map((track, idx) => (
            <button
              key={idx}
              onClick={() => handleDownload(track.url, track.title)}
              className="w-full py-3 bg-stone-700 text-white rounded-xl hover:bg-stone-600 transition-colors flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              {track.title}
            </button>
          ))}
        </div>
      )}

      {/* Receipt link */}
      {data.receipt_url && (
        <a
          href={data.receipt_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full mt-4 py-3 bg-stone-700 text-white rounded-xl hover:bg-stone-600 transition-colors text-center"
        >
          View Receipt
        </a>
      )}

      <p className="text-xs text-muted-foreground mt-6">
        Order ID: {data.order_id}
      </p>
    </motion.div>
  );
};

// Main Quick Buy Page
const QuickBuyPage = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchaseData, setPurchaseData] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [audio] = useState(new Audio());
  const [selectedFormat, setSelectedFormat] = useState('mp3');
  const [adminGranting, setAdminGranting] = useState(false);

  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;

  // Check if WAV is available
  const hasWav = product?.wav_url && product?.wav_price;
  const currentPrice = selectedFormat === 'wav' && hasWav ? product.wav_price : product?.price;

  useEffect(() => {
    fetchProduct();
    return () => {
      audio.pause();
    };
  }, [type, id]);

  const fetchProduct = async () => {
    try {
      const response = await fetch(`${API}/api/quick-buy/${type}/${id}`);
      if (!response.ok) {
        throw new Error('Product not found');
      }
      const data = await response.json();
      setProduct(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePreview = () => {
    if (!product.preview_url) return;
    
    if (playing) {
      audio.pause();
    } else {
      audio.src = product.preview_url;
      audio.play();
    }
    setPlaying(!playing);
  };

  const handleAdminFreeDownload = async () => {
    setAdminGranting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = type === 'track'
        ? `${API}/api/quick-buy/admin/free-download/track/${id}?format=${selectedFormat}`
        : `${API}/api/quick-buy/admin/free-download/album/${id}`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Admin download failed');
      setPurchaseData(data);
      toast.success('Admin download granted — no payment charged');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdminGranting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
          <Music className="w-12 h-12 text-primary" />
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center p-6">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Product Not Found</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <button
            onClick={() => navigate('/music')}
            className="px-6 py-3 bg-primary text-black font-bold rounded-xl"
          >
            Browse Music
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-900 via-stone-900 to-black flex items-center justify-center p-4">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md"
      >
        {/* Product Card */}
        <div className="bg-stone-800/50 backdrop-blur-xl rounded-3xl border border-stone-700 overflow-hidden">
          {/* Cover Art */}
          <div className="relative aspect-square">
            {product.cover_art ? (
              <img loading="lazy" decoding="async" 
                src={product.cover_art} 
                alt={product.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary/20 to-amber-500/20 flex items-center justify-center">
                {type === 'track' ? (
                  <Music className="w-24 h-24 text-primary/50" />
                ) : (
                  <Disc className="w-24 h-24 text-primary/50" />
                )}
              </div>
            )}
            
            {/* Preview button */}
            {product.preview_url && (
              <button
                onClick={togglePreview}
                className="absolute bottom-4 right-4 w-14 h-14 bg-black/70 backdrop-blur rounded-full flex items-center justify-center hover:bg-black/90 transition-colors"
              >
                {playing ? (
                  <Pause className="w-6 h-6 text-white" />
                ) : (
                  <Play className="w-6 h-6 text-white ml-1" />
                )}
              </button>
            )}

            {/* Price badge */}
            <div className={`absolute top-4 right-4 px-4 py-2 font-bold rounded-full text-lg ${
              selectedFormat === 'wav' && hasWav ? 'bg-purple-600 text-white' : 'bg-primary text-black'
            }`}>
              ${currentPrice?.toFixed(2)}
              {hasWav && <span className="text-xs ml-1 uppercase">{selectedFormat}</span>}
            </div>
            
            {/* WAV badge */}
            {hasWav && (
              <div className="absolute top-4 left-4 px-3 py-1 bg-purple-600/90 text-white text-xs font-bold rounded-full">
                WAV Available
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                {type === 'track' ? (
                  <Music className="w-5 h-5 text-primary" />
                ) : (
                  <Disc className="w-5 h-5 text-primary" />
                )}
              </div>
              <div>
                <p className="text-xs text-primary uppercase font-medium">
                  {type === 'track' ? 'Single Track' : `Album • ${product.track_count} tracks`}
                </p>
                <h1 className="text-xl font-bold text-white">{product.title}</h1>
                <p className="text-muted-foreground">{product.artist}</p>
              </div>
            </div>

            {/* Album tracks preview */}
            {type === 'album' && product.tracks && (
              <div className="mb-6 max-h-32 overflow-y-auto">
                {product.tracks.slice(0, 5).map((track, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground py-1">
                    <span className="text-xs text-stone-500">{idx + 1}.</span>
                    <span className="truncate">{track.title}</span>
                  </div>
                ))}
                {product.tracks.length > 5 && (
                  <p className="text-xs text-stone-500">+{product.tracks.length - 5} more tracks</p>
                )}
              </div>
            )}

            {/* Format Selector for tracks */}
            {type === 'track' && hasWav && !purchaseData && (
              <div className="mb-6">
                <p className="text-sm text-muted-foreground mb-2">Select Format:</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedFormat('mp3')}
                    className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all ${
                      selectedFormat === 'mp3'
                        ? 'bg-primary text-black'
                        : 'bg-stone-700 text-stone-300 hover:bg-stone-600'
                    }`}
                  >
                    <div className="text-lg">MP3</div>
                    <div className="text-sm opacity-80">${product.price?.toFixed(2)}</div>
                  </button>
                  <button
                    onClick={() => setSelectedFormat('wav')}
                    className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all ${
                      selectedFormat === 'wav'
                        ? 'bg-purple-600 text-white'
                        : 'bg-stone-700 text-stone-300 hover:bg-stone-600'
                    }`}
                  >
                    <div className="text-lg">WAV</div>
                    <div className="text-sm opacity-80">${product.wav_price?.toFixed(2)}</div>
                    <div className="text-xs opacity-60">Lossless</div>
                  </button>
                </div>
              </div>
            )}

            {/* Payment or Success */}
            {purchaseData ? (
              <DownloadSuccess data={purchaseData} product={product} />
            ) : (
              <>
                {/* Admin free-download shortcut (only visible when logged in as admin) */}
                {isAdminUser && (
                  <div className="mb-4 p-4 rounded-xl border-2 border-yellow-500/50 bg-yellow-500/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="w-5 h-5 text-yellow-400" />
                      <span className="text-yellow-300 font-bold text-sm uppercase tracking-wider">Admin Access</span>
                    </div>
                    <p className="text-xs text-yellow-100/80 mb-3">
                      Skip payment and grab this {type} for free to verify the download works.
                    </p>
                    <button
                      type="button"
                      onClick={handleAdminFreeDownload}
                      disabled={adminGranting}
                      data-testid="admin-free-download-btn"
                      className="w-full py-3 rounded-xl font-bold bg-yellow-500 hover:bg-yellow-400 text-black transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {adminGranting ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Preparing download…</>
                      ) : (
                        <><Download className="w-4 h-4" /> Get Free Admin Download</>
                      )}
                    </button>
                  </div>
                )}

                {stripePromise ? (
                  <Elements stripe={stripePromise}>
                    <PaymentForm 
                      product={{...product, price: currentPrice, format: selectedFormat}} 
                      onSuccess={(data) => {
                        setPurchaseData(data);
                        toast.success('Purchase successful!');
                      }}
                    />
                  </Elements>
                ) : (
                  <div className="p-4 rounded-xl border border-red-500/40 bg-red-500/10 text-red-300 text-sm">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    Card payment is temporarily unavailable — the Stripe publishable key is missing on this environment. Please contact support.
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Casa Samba branding */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Escola de Samba Casa Samba
        </p>
      </motion.div>
    </div>
  );
};

export default QuickBuyPage;
